import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import '/node_modules/react-pdf/dist/esm/Page/AnnotationLayer.css';
import '/node_modules/react-pdf/dist/esm/Page/TextLayer.css';

// Set up the worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

interface PDFViewerProps {
  file: string | File;
  width?: number;
  height?: number;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  file,
  width = 800,
  height = 600
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setError(error.message);
    setLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const downloadPDF = () => {
    if (typeof file === 'string') {
      const link = document.createElement('a');
      link.href = file;
      link.download = 'document.pdf';
      link.click();
    }
  };

  return (
    <div className="pdf-viewer">
      <Row className="mb-3">
        <Col className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1 || loading}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>

            <span className="mx-2">
              Page {pageNumber} of {numPages || '--'}
            </span>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={goToNextPage}
              disabled={pageNumber >= (numPages || 1) || loading}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={downloadPDF}
            disabled={loading || !file}
          >
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Download
          </Button>
        </Col>
      </Row>

      <div
        className="pdf-container border rounded"
        style={{ height: `${height}px`, overflow: 'auto' }}
      >
        {loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {error && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="text-danger">Error loading PDF: {error}</div>
          </div>
        )}

        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
        >
          <Page
            pageNumber={pageNumber}
            width={width}
            renderAnnotationLayer={true}
            renderTextLayer={true}
          />
        </Document>
      </div>
    </div>
  );
};

export default PDFViewer;
